/**
 * Os números de uma linha: os 32 que a API manda — 12 de receita, 10 contadores de envio e 10
 * taxas — mais o eCPM, que não vem no contrato e sai da divisão de dois deles.
 *
 * Duas regras do contrato moram aqui, porque são de rótulo e não de cálculo:
 * - cada taxa tem um denominador próprio e a API já a calculou — nunca recalcular nem fundir duas
 *   taxas parecidas (`den` existe para o rótulo dizer a diferença);
 * - `null` é ausência, não zero: quem formata devolve "—" (ver `lib/format.ts`).
 */
import type { Hue } from '@/components/v2/theme';
import type { Metrics, Rates, Revenue, RevenueDay, Sends } from '@/types/email-v1';

export type Block = 'revenue' | 'sends' | 'rates';
export type Fmt = 'brl' | 'int' | 'pct1';

/** Uma linha com o trio de blocos — qualquer um deles pode vir `null` pelo contrato. */
export type Holder = { revenue?: Revenue | null; sends?: Sends | null; rates?: Rates | null };

export interface Metric {
    /** Chave da coluna; é o nome do campo dentro do bloco. */
    k: string;
    block: Block;
    l: string;
    /** O denominador, para taxas; entra no rótulo quando duas versões aparecem juntas. */
    den?: string;
    /** Explicação do seletor de colunas e do tooltip. */
    d: string;
    fmt: Fmt;
    hue?: Hue;
    /** Taxa cujo irmão só difere no denominador: juntas, as duas pedem o denominador no rótulo. */
    twin?: string;
    /**
     * Número que a API NÃO manda: sai da divisão de dois que ela mandou. Quem lê chama isto no
     * lugar de procurar o campo no bloco — e, como a conta roda na linha já somada, o grupo sai
     * certo sozinho, sem caso especial em `aggregate`.
     */
    calc?: (row: Holder) => number | null;
    /**
     * Menos é melhor: bounce, reclamação, descadastro. O mapa de calor pinta acima da média de
     * verde e abaixo de vermelho — sem esta marca, um bounce alto sairia verde, que é o contrário
     * do que ele quer dizer. É explícito, e não deduzido da cor: `hue` diz o TOM da métrica, não a
     * direção dela.
     */
    down?: true;
}

const m = (x: Metric) => x;

export const METRICS: Metric[] = [
    // ---------------------------------------------------------------- receita (12)
    m({ k: 'net', block: 'revenue', l: 'Receita líquida', d: 'O que sobra: bruta menos revshare, spread e imposto.', fmt: 'brl', hue: 'good' }),
    m({ k: 'rev', block: 'revenue', l: 'Receita bruta', d: 'A receita antes de qualquer desconto.', fmt: 'brl', hue: 'good' }),
    m({ k: 'revshare', block: 'revenue', l: 'Revshare', d: 'A parte do parceiro, descontada da bruta.', fmt: 'brl', hue: 'cyan' }),
    m({ k: 'spread', block: 'revenue', l: 'Spread', d: 'O seu spread — muda de pessoa para pessoa olhando a mesma tela.', fmt: 'brl', hue: 'violet' }),
    m({ k: 'taxRev', block: 'revenue', l: 'Imposto', d: 'O imposto descontado da receita.', fmt: 'brl', hue: 'amber' }),
    m({
        k: 'ecpm', block: 'revenue', l: 'eCPM', fmt: 'brl', hue: 'good',
        d: 'Receita líquida ÷ impressões × 1.000 — o que mil impressões rendem DEPOIS de revshare, spread e imposto. O GAM mostra o eCPM da bruta, que é maior.',
        // sem impressão não há eCPM: zero impressões não é "zero reais por mil", é pergunta sem resposta
        calc: (r) => (r.revenue && r.revenue.gamImp > 0 ? (r.revenue.net / r.revenue.gamImp) * 1000 : null),
    }),
    m({ k: 'gamImp', block: 'revenue', l: 'Impressões', d: 'Impressões de anúncio no GAM.', fmt: 'int' }),
    m({ k: 'gamClicks', block: 'revenue', l: 'Cliques no anúncio', d: 'Cliques em anúncio no GAM — não é o clique no e-mail.', fmt: 'int' }),
    m({ k: 'gamViewable', block: 'revenue', l: 'Visíveis', d: 'Impressões visíveis, pelo critério do GAM.', fmt: 'int' }),
    m({ k: 'gamMeasurable', block: 'revenue', l: 'Mensuráveis', d: 'Impressões que o GAM conseguiu medir.', fmt: 'int' }),
    m({ k: 'gamUnfilled', block: 'revenue', l: 'Não preenchidas', d: 'Requisições sem anúncio para entregar.', fmt: 'int' }),
    m({ k: 'gamServed', block: 'revenue', l: 'Servidas', d: 'Requisições atendidas pelo GAM.', fmt: 'int' }),
    m({ k: 'gamEligible', block: 'revenue', l: 'Elegíveis', d: 'Requisições elegíveis a anúncio.', fmt: 'int' }),

    // ---------------------------------------------------------------- envios (10)
    m({ k: 'sent', block: 'sends', l: 'Enviados', d: 'Mensagens que saíram.', fmt: 'int' }),
    m({ k: 'delivered', block: 'sends', l: 'Entregues', d: 'Mensagens aceitas pelo destino.', fmt: 'int', hue: 'cyan' }),
    m({ k: 'opens', block: 'sends', l: 'Aberturas', d: 'Eventos de abertura — a mesma pessoa conta mais de uma vez.', fmt: 'int' }),
    m({ k: 'uniqueOpens', block: 'sends', l: 'Aberturas únicas', d: 'Pessoas distintas que abriram.', fmt: 'int', hue: 'violet' }),
    m({ k: 'clicks', block: 'sends', l: 'Cliques', d: 'Eventos de clique no e-mail — a mesma pessoa conta mais de uma vez.', fmt: 'int' }),
    m({ k: 'uniqueClicks', block: 'sends', l: 'Cliques únicos', d: 'Pessoas distintas que clicaram.', fmt: 'int', hue: 'pink' }),
    m({ k: 'bounces', block: 'sends', l: 'Bounces', d: 'Recusas permanentes.', fmt: 'int', hue: 'bad', down: true }),
    m({ k: 'softBounces', block: 'sends', l: 'Soft bounces', d: 'Recusas temporárias.', fmt: 'int', hue: 'amber', down: true }),
    m({ k: 'complaints', block: 'sends', l: 'Reclamações', d: 'Marcações de spam.', fmt: 'int', hue: 'bad', down: true }),
    m({ k: 'unsubscribes', block: 'sends', l: 'Descadastros', d: 'Pedidos de saída da lista.', fmt: 'int', hue: 'amber', down: true }),

    // ---------------------------------------------------------------- taxas (10)
    m({ k: 'deliveryRate', block: 'rates', l: 'Entrega', den: 'dos enviados', d: 'entregues ÷ enviados', fmt: 'pct1', hue: 'cyan' }),
    m({ k: 'openRate', block: 'rates', l: 'Abertura', den: 'dos entregues', d: 'aberturas únicas ÷ entregues', fmt: 'pct1', hue: 'violet', twin: 'effectiveOpenRate' }),
    m({ k: 'clickRate', block: 'rates', l: 'Clique', den: 'dos entregues', d: 'cliques únicos ÷ entregues', fmt: 'pct1', hue: 'pink', twin: 'effectiveClickRate' }),
    m({ k: 'clickToOpenRate', block: 'rates', l: 'Clique sobre abertura', den: 'das aberturas', d: 'cliques únicos ÷ aberturas únicas', fmt: 'pct1', hue: 'pink' }),
    m({ k: 'effectiveOpenRate', block: 'rates', l: 'Abertura efetiva', den: 'dos enviados', d: 'aberturas únicas ÷ enviados', fmt: 'pct1', hue: 'violet', twin: 'openRate' }),
    m({ k: 'effectiveClickRate', block: 'rates', l: 'Clique efetivo', den: 'dos enviados', d: 'cliques únicos ÷ enviados', fmt: 'pct1', hue: 'pink', twin: 'clickRate' }),
    m({ k: 'bounceRate', block: 'rates', l: 'Bounce', den: 'dos enviados', d: 'bounces ÷ enviados', fmt: 'pct1', hue: 'bad', down: true }),
    m({ k: 'softBounceRate', block: 'rates', l: 'Soft bounce', den: 'dos enviados', d: 'soft bounces ÷ enviados', fmt: 'pct1', hue: 'amber', down: true }),
    m({ k: 'complaintRate', block: 'rates', l: 'Reclamação', den: 'dos enviados', d: 'reclamações ÷ enviados', fmt: 'pct1', hue: 'bad', down: true }),
    m({ k: 'unsubscribeRate', block: 'rates', l: 'Descadastro', den: 'dos enviados', d: 'descadastros ÷ enviados', fmt: 'pct1', hue: 'amber', down: true }),
];

export const M: Record<string, Metric> = Object.fromEntries(METRICS.map((x) => [x.k, x]));

/**
 * As métricas que um gráfico oferece para escolher. Não são as 33: num seletor de eixo, a lista
 * inteira vira uma parede de botões e as doze que se olha todo dia somem no meio.
 */
export const CHART_METRICS = [
    'net', 'rev', 'ecpm', 'sent', 'delivered', 'uniqueOpens', 'uniqueClicks',
    'deliveryRate', 'openRate', 'clickRate', 'bounceRate', 'unsubscribeRate',
].filter((k) => M[k]);

export const BLOCK_LABEL: Record<Block, string> = { revenue: 'Receita', sends: 'Envios', rates: 'Taxas' };
/** Cor de cada bloco, para a faixa de "Separar por origem" (mesmo recurso da tabela da Meta Geral). */
export const BLOCK_HUE: Record<Block, Hue> = { revenue: 'good', sends: 'cyan', rates: 'violet' };
export const BLOCKS: Block[] = ['revenue', 'sends', 'rates'];

/**
 * Conjunto padrão das listas: identificação + estes seis. Cobre "funcionou?" nas duas metades —
 * engajamento e dinheiro — sem passar de sete colunas.
 */
export const DEFAULT_COLUMNS = ['sent', 'deliveryRate', 'openRate', 'clickRate', 'unsubscribeRate', 'net'];

/** Atalhos do seletor de colunas. */
export const COLUMN_SETS: { k: string; l: string; cols: string[] }[] = [
    { k: 'default', l: 'Padrão', cols: DEFAULT_COLUMNS },
    { k: 'deliver', l: 'Entregabilidade', cols: ['sent', 'delivered', 'deliveryRate', 'bounceRate', 'softBounceRate', 'complaintRate', 'unsubscribeRate'] },
    { k: 'revenue', l: 'Receita', cols: ['sent', 'net', 'ecpm', 'gamImp', 'rev', 'revshare', 'spread', 'taxRev'] },
    { k: 'engage', l: 'Engajamento', cols: ['sent', 'uniqueOpens', 'uniqueClicks', 'openRate', 'clickRate', 'clickToOpenRate'] },
];

/**
 * O rótulo do cabeçalho. Quando a coluna irmã também está visível, o denominador entra no nome:
 * "Abertura" e "Abertura efetiva" lado a lado, sem o denominador, passam por erro de cálculo.
 */
export const headerLabel = (key: string, visible: readonly string[]): { l: string; den?: string } => {
    const met = M[key];
    if (!met) return { l: key };
    const needsDen = !!met.twin && visible.includes(met.twin);
    return { l: met.l, den: needsDen ? met.den : undefined };
};

/** Lê o valor de uma métrica num trio `{revenue, sends, rates}`, aceitando bloco inteiro `null`. */
export const readMetric = (row: Holder, key: string): number | null => {
    const met = M[key];
    if (!met) return null;
    if (met.calc) return met.calc(row);
    const block = row[met.block] as Record<string, number | null> | null | undefined;
    if (!block) return null;
    const value = block[met.k];
    return typeof value === 'number' ? value : null;
};

/** O mesmo, para uma linha que sempre tem o trio completo. */
export const readMetrics = (row: Metrics, key: string) => readMetric(row, key);

/* ----------------------------------------------------------------- agrupamento no front */

const ZERO_REVENUE: Revenue = {
    rev: 0, revshare: 0, spread: 0, taxRev: 0, net: 0, gamImp: 0, gamClicks: 0,
    gamViewable: 0, gamMeasurable: 0, gamUnfilled: 0, gamServed: 0, gamEligible: 0,
};
const ZERO_SENDS: Sends = {
    sent: 0, delivered: 0, opens: 0, uniqueOpens: 0, clicks: 0, uniqueClicks: 0,
    bounces: 0, softBounces: 0, complaints: 0, unsubscribes: 0,
};

/** Divisão com denominador zero → `null`. Em por cento, com uma casa, como a API devolve. */
const ratio = (a: number, b: number): number | null => (b > 0 ? Math.round((a / b) * 1000) / 10 : null);

/**
 * Taxas a partir dos contadores somados. Quando a TELA agrupa linhas (por categoria, por provedor),
 * não existe taxa pronta para o grupo e a média das taxas das linhas mentiria — então deriva-se dos
 * totais, pelos mesmos denominadores do contrato, e a célula ganha a marca de recalculada.
 */
export const derivedRates = (s: Sends): Rates => ({
    deliveryRate: ratio(s.delivered, s.sent),
    openRate: ratio(s.uniqueOpens, s.delivered),
    clickRate: ratio(s.uniqueClicks, s.delivered),
    clickToOpenRate: ratio(s.uniqueClicks, s.uniqueOpens),
    effectiveOpenRate: ratio(s.uniqueOpens, s.sent),
    effectiveClickRate: ratio(s.uniqueClicks, s.sent),
    bounceRate: ratio(s.bounces, s.sent),
    softBounceRate: ratio(s.softBounces, s.sent),
    complaintRate: ratio(s.complaints, s.sent),
    unsubscribeRate: ratio(s.unsubscribes, s.sent),
});

/** Soma um grupo de linhas. As taxas vêm de `derivedRates`, nunca de média. */
/**
 * As séries por dia de várias linhas somadas numa só, por data — o que `aggregate` faz com o bloco do
 * período, para a linha agrupada ter curva como tem cartão.
 *
 * Linha sem série (ou com `null`, que é a rota de domínio dividido) não entra: ela não tem receita a somar,
 * e inventar zeros ali afirmaria uma medida que não existe.
 */
export const mergeDaily = (series: readonly (RevenueDay[] | null | undefined)[]): RevenueDay[] => {
    const byDay = new Map<string, RevenueDay>();
    for (const list of series) {
        for (const p of list ?? []) {
            const found = byDay.get(p.date);
            if (found) { found.net += p.net; found.rev += p.rev; } else { byDay.set(p.date, { ...p }); }
        }
    }
    return [...byDay.values()].sort((a, b) => a.date.localeCompare(b.date));
};

export const aggregate = (rows: readonly { revenue: Revenue | null; sends: Sends | null }[]): Metrics => {
    const revenue = { ...ZERO_REVENUE };
    const sends = { ...ZERO_SENDS };
    for (const row of rows) {
        if (row.revenue) for (const k of Object.keys(revenue) as (keyof Revenue)[]) revenue[k] += row.revenue[k];
        if (row.sends) for (const k of Object.keys(sends) as (keyof Sends)[]) sends[k] += row.sends[k];
    }
    return { revenue, sends, rates: derivedRates(sends) };
};
